// ===============================================================
// File Library — Document Detail
// Full document view with metadata, actions, and download
// ===============================================================

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Title, Text, Paper, Badge, Button, Group, SimpleGrid,
  Center, Loader, Modal,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft, IconDownload, IconExternalLink, IconPencil,
  IconTrash, IconAlertTriangle,
} from '@tabler/icons-react';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import type { FLCategory } from '../../store/fileLibraryStore';
import { WORKER_URL } from '../../lib/api-switch';

function getCatColor(catName: string, categories: FLCategory[]): string {
  const cat = categories.find(c => c.name === catName);
  return cat?.color || '#64748B';
}

function getPriorityColor(priority: string): string {
  const p = (priority || '').toLowerCase();
  if (p === 'critical') return 'red';
  if (p === 'important') return 'orange';
  return 'gray';
}

function getStatusColor(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'gray';
  return 'green';
}

export default function DocDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { documents, categories, loading, loadDocuments, loadCategories, deleteDocument } = useFileLibraryStore();
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadDocuments();
    loadCategories();
  }, [loadDocuments, loadCategories]);

  const doc = documents.find(d => String(d.id) === id);

  if (loading && !doc) {
    return <Center py="xl"><Loader size="sm" /></Center>;
  }

  if (!doc) {
    return (
      <div>
        <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate('/filelibrary/browse')} mb="md">
          Back to Browse
        </Button>
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">Document not found.</Text>
        </Paper>
      </div>
    );
  }

  // Expiration check
  let expirationWarning: string | null = null;
  if (doc.expirationDate) {
    const exp = new Date(doc.expirationDate);
    if (!isNaN(exp.getTime())) {
      const now = new Date();
      const daysUntil = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) {
        expirationWarning = `This document expired on ${exp.toLocaleDateString()}.`;
      } else if (daysUntil < 30) {
        expirationWarning = `This document expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''} (${exp.toLocaleDateString()}).`;
      }
    }
  }

  const tagList = doc.tags
    ? doc.tags.split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDocument(doc.id);
      notifications.show({ title: 'Deleted', message: 'Document deleted.', color: 'green' });
      navigate('/filelibrary/browse');
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to delete document.',
        color: 'red',
      });
    } finally {
      setDeleting(false);
      closeDelete();
    }
  };

  return (
    <div>
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={14} />}
        onClick={() => navigate('/filelibrary/browse')}
        mb="md"
        size="xs"
      >
        Back to Browse
      </Button>

      {/* Expiration warning banner */}
      {expirationWarning && (
        <Paper
          p="sm"
          mb="md"
          radius="sm"
          style={{
            background: expirationWarning.includes('expired') ? '#fef2f2' : '#fefce8',
            border: `1px solid ${expirationWarning.includes('expired') ? '#fca5a5' : '#fde047'}`,
          }}
        >
          <Group gap="xs">
            <IconAlertTriangle size={16} color={expirationWarning.includes('expired') ? '#dc2626' : '#ca8a04'} />
            <Text size="sm" fw={500} c={expirationWarning.includes('expired') ? 'red' : 'yellow.8'}>
              {expirationWarning}
            </Text>
          </Group>
        </Paper>
      )}

      {/* Title and badges */}
      <Group gap="sm" mb="md" align="center">
        <Title order={3}>{doc.title}</Title>
        {doc.category && (
          <Badge style={{ background: getCatColor(doc.category, categories), color: '#fff' }}>
            {doc.category}
          </Badge>
        )}
        {doc.priority && doc.priority !== 'Normal' && (
          <Badge color={getPriorityColor(doc.priority)} variant="outline">{doc.priority}</Badge>
        )}
        <Badge color={getStatusColor(doc.status)}>{doc.status || 'Published'}</Badge>
      </Group>

      {/* Description */}
      {doc.description && (
        <Paper p="md" radius="sm" withBorder mb="md">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{doc.description}</Text>
        </Paper>
      )}

      {/* Meta grid */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} mb={4}>Published Date</Text>
          <Text size="sm">{doc.publishDate ? new Date(doc.publishDate).toLocaleDateString() : '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} mb={4}>Expiration Date</Text>
          <Text size="sm">{doc.expirationDate && !isNaN(new Date(doc.expirationDate).getTime()) ? new Date(doc.expirationDate).toLocaleDateString() : 'None'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} mb={4}>Version</Text>
          <Text size="sm">{doc.version || '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} mb={4}>File Type</Text>
          <Text size="sm" tt="uppercase">{doc.fileType || '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} mb={4}>Uploaded By</Text>
          <Text size="sm">{doc.uploadedBy || '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} mb={4}>Status</Text>
          <Badge color={getStatusColor(doc.status)}>{doc.status || 'Published'}</Badge>
        </Paper>
      </SimpleGrid>

      {/* Tags */}
      {tagList.length > 0 && (
        <Group gap="xs" mb="md">
          <Text size="xs" c="dimmed" fw={600}>Tags:</Text>
          {tagList.map(tag => (
            <Badge key={tag} size="sm" variant="outline" color="blue">{tag}</Badge>
          ))}
        </Group>
      )}

      {/* Action buttons */}
      <Group gap="sm" mb="md">
        {doc.fileUploadUrl && (
          <Button
            leftSection={<IconDownload size={14} />}
            size="xs"
            component="a"
            href={`${WORKER_URL}/api/ignatius/api/fileview/${doc.fileUploadUrl}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </Button>
        )}
        {doc.fileUrl && (
          <Button
            leftSection={<IconExternalLink size={14} />}
            size="xs"
            variant="outline"
            component="a"
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Link
          </Button>
        )}
        <Button
          leftSection={<IconPencil size={14} />}
          size="xs"
          variant="outline"
          onClick={() => navigate(`/filelibrary/publish/${doc.id}`)}
        >
          Edit
        </Button>
        <Button
          leftSection={<IconTrash size={14} />}
          size="xs"
          variant="outline"
          color="red"
          onClick={openDelete}
        >
          Delete
        </Button>
      </Group>

      {/* Delete Confirmation */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Document" size="sm">
        <Text size="sm" mb="md">Delete "{doc.title}"? This cannot be undone.</Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="red" size="xs" onClick={handleDelete} loading={deleting}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
